import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import LoadingScreen from './LoadingScreen'; // Assuming you have a LoadingScreen component

const PrivateRoute = ({ children }) => {
  const { user, loading: authLoading, updateUser } = useAuth();
  const location = useLocation();

  // State to track onboarding status
  // null: unknown, true: complete, false: incomplete
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);

  useEffect(() => {
    if (authLoading) {
      // Wait for authentication to resolve
      return;
    }

    if (!user) {
      // If no user after auth loading, no need to check onboarding
      setIsCheckingStatus(false);
      setOnboardingStatus(false); // Treat as incomplete for non-logged-in users (though they'll be redirected to login)
      return;
    }

    // Check if user object from context already has reliable onboarding info
    // This depends on how AuthContext populates user (e.g., from JWT claims or initial profile fetch)
    if (user.preferences && typeof user.preferences.has_completed_onboarding !== 'undefined') {
      setOnboardingStatus(user.preferences.has_completed_onboarding === 1);
      setIsCheckingStatus(false);
    } else {
      // If not in context, fetch from profile API
      const fetchOnboardingStatus = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/auth/profile`,
            { headers: { Authorization: `Bearer ${user.token}` } }
          );
          if (response.data.user) {
            const isComplete = response.data.user.has_completed_onboarding === 1;
            setOnboardingStatus(isComplete);
            // Optionally update the user context if it was missing this info
            if (updateUser && user.preferences?.has_completed_onboarding !== response.data.user.has_completed_onboarding) {
                updateUser({ 
                    ...user, 
                    preferences: { 
                        ...user.preferences, 
                        has_completed_onboarding: response.data.user.has_completed_onboarding 
                    }
                });
            }
          } else {
            setOnboardingStatus(false); // Default to incomplete if profile fetch issue
          }
        } catch (error) {
          console.error('Error fetching onboarding status in PrivateRoute:', error);
          setOnboardingStatus(false); // Default to incomplete on error
        } finally {
          setIsCheckingStatus(false);
        }
      };
      fetchOnboardingStatus();
    }
  }, [user, authLoading, updateUser]);

  if (authLoading || isCheckingStatus) {
    return <LoadingScreen message="Checking your access..." />;
  }

  if (!user) {
    // This redirection should ideally happen before onboarding checks if authLoading is false and no user
    console.log('PrivateRoute: User not authenticated, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // User is authenticated, now check onboarding status
  if (onboardingStatus === false) {
    // If already on the onboarding page, or trying to access it, allow it.
    if (location.pathname === '/onboarding') {
      console.log('PrivateRoute: Onboarding incomplete, already on /onboarding. Allowing.');
      return children;
    }
    console.log('PrivateRoute: Onboarding incomplete, redirecting to /onboarding');
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }

  if (onboardingStatus === true) {
     // If user is onboarded but tries to access /onboarding, redirect them to dashboard
    if (location.pathname === '/onboarding') {
      console.log('PrivateRoute: Onboarding complete, redirecting from /onboarding to dashboard.');
      return <Navigate to="/dashboard/home" replace />;
    }
    console.log('PrivateRoute: User authenticated and onboarding complete. Rendering protected content.');
    return children;
  }
  
  // Fallback or if onboardingStatus is still null (should be handled by LoadingScreen)
  console.log('PrivateRoute: Fallback, onboarding status unknown after checks.');
  return <LoadingScreen message="Verifying session..." />; 
};

export default PrivateRoute; 